import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'brass';
type Size = 'sm' | 'md' | 'lg';
const base = 'inline-flex items-center justify-center gap-2 rounded-md font-medium tracking-tight transition-all duration-200 focus-visible:outline-none disabled:opacity-60 disabled:pointer-events-none whitespace-nowrap';
const variants: Record<Variant, string> = {
  primary: 'bg-brass text-white hover:bg-brass-dark shadow-sm hover:shadow-md',
  secondary: 'bg-navy text-cream hover:bg-navy-700 shadow-sm',
  outline: 'border border-navy/25 text-navy hover:border-navy hover:bg-navy/[0.03]',
  ghost: 'text-navy hover:bg-navy/[0.05]',
  brass: 'border border-brass/60 text-brass-dark hover:bg-brass hover:text-white'
};
const sizes: Record<Size, string> = {
  sm: 'text-sm px-3.5 py-2',
  md: 'text-[0.95rem] px-5 py-2.5',
  lg: 'text-base px-6 py-3.5'
};
type CommonProps = {
  variant?: Variant;
  size?: Size;
  className?: string;
  children: React.ReactNode;
};
type ButtonAsButton = CommonProps & React.ButtonHTMLAttributes<HTMLButtonElement> & {
  to?: undefined;
  href?: undefined;
};
type ButtonAsLink = CommonProps & {
  to: string;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;
type ButtonAsAnchor = CommonProps & {
  href: string;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;
export function Button(props: ButtonAsButton | ButtonAsLink | ButtonAsAnchor) {
  const {
    variant = 'primary',
    size = 'md',
    className,
    children
  } = props;
  const classes = cn(base, variants[variant], sizes[size], className);
  if ('to' in props && props.to !== undefined) {
    const {
      variant: _v,
      size: _s,
      className: _c,
      children: _ch,
      to,
      ...rest
    } = props as ButtonAsLink;
    return <Link to={to} className={classes} {...rest} data-id="element-467">
        {children}
      </Link>;
  }
  if ('href' in props && props.href !== undefined) {
    const {
      variant: _v,
      size: _s,
      className: _c,
      children: _ch,
      href,
      ...rest
    } = props as ButtonAsAnchor;
    return <a href={href} className={classes} {...rest} data-id="element-468">
        {children}
      </a>;
  }
  const {
    variant: _v,
    size: _s,
    className: _c,
    children: _ch,
    ...rest
  } = props as ButtonAsButton;
  return <button className={classes} {...rest} data-id="element-469">
      {children}
    </button>;
}